import React from "react";
import { Link } from "react-router-dom";
import { RxCross1 } from "react-icons/rx";

const formatMoney = (v) => `£${Number(v || 0).toFixed(2)}`;
const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleString("en-GB");
};

function DetailRow({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-4 py-2 border-b border-slate-100 last:border-0">
      <dt className="text-sm font-medium text-slate-500 sm:w-40 shrink-0">{label}</dt>
      <dd className="text-sm text-slate-900 break-all">{value ?? "—"}</dd>
    </div>
  );
}

export function TransactionDetailModal({ open, transaction, onClose }) {
  if (!open || !transaction) return null;

  const tx = transaction;
  const isOrder = tx.type === "marketplace_order";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#38513B]">
              {tx.typeLabel}
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-1">{tx.reference}</h3>
            <p className="text-sm text-slate-500 mt-1">{formatDate(tx.date)}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100">
            <RxCross1 size={22} />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          <dl className="rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2">
            <DetailRow label="Gross amount" value={formatMoney(tx.grossAmount)} />
            <DetailRow label="VA earnings (est.)" value={formatMoney(tx.vaEarnings)} />
            {isOrder ? (
              <>
                <DetailRow label="VA fees (gross)" value={formatMoney(tx.vaFeesGross)} />
                <DetailRow label="Buyer protection" value={formatMoney(tx.buyerProtectionFee)} />
                <DetailRow label="Platform fee" value={formatMoney(tx.platformFeeAmount)} />
                <DetailRow label="Featured on order" value={formatMoney(tx.featuredItemFee)} />
                <DetailRow label="Seller payout (est.)" value={formatMoney(tx.sellerPayout)} />
              </>
            ) : null}
            <DetailRow label="Payment method" value={String(tx.paymentMethod || "—")} />
            <DetailRow label="Status" value={tx.status} />
            {tx.paymentIntentId ? (
              <DetailRow label="Stripe PI" value={tx.paymentIntentId} />
            ) : null}
            {tx.note ? <DetailRow label="Note" value={tx.note} /> : null}
          </dl>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Buyer</p>
              <p className="font-medium text-slate-900">{tx.buyerName}</p>
              {tx.buyerEmail ? (
                <p className="text-sm text-slate-600 mt-1">{tx.buyerEmail}</p>
              ) : null}
            </div>
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">
                {isOrder ? "Seller" : "Paid by / listing"}
              </p>
              <p className="font-medium text-slate-900">{tx.sellerName}</p>
              {tx.sellerEmail ? (
                <p className="text-sm text-slate-600 mt-1">{tx.sellerEmail}</p>
              ) : null}
            </div>
          </div>

          {isOrder && tx.cartSummary ? (
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Items</p>
              <p className="text-sm text-slate-800">{tx.cartSummary}</p>
              <p className="text-xs text-slate-500 mt-2">{tx.itemCount} item(s) in order</p>
            </div>
          ) : null}

          {tx.durationWeeks ? (
            <p className="text-sm text-slate-600">
              Featured duration: <strong>{tx.durationWeeks} week(s)</strong>
            </p>
          ) : null}

          {isOrder && tx.orderId ? (
            <Link
              to={`/admin-order/${tx.orderId}`}
              className="inline-flex items-center justify-center rounded-lg bg-[#38513B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2f4232]"
            >
              Open full order in admin
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export const DATE_FILTER_LABELS = {
  all: "All time",
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  annually: "Yearly",
  range: "Custom range",
};

export function filterTransactionsForMetric(metricId, transactions = []) {
  const list = Array.isArray(transactions) ? transactions : [];
  if (!metricId) return list;
  switch (metricId) {
    case "totalSales":
    case "totalOrders":
    case "avgOrderValue":
    case "buyerProtection":
    case "sellerPlatform":
    case "vaPendingEscrow":
    case "vaReleased":
      return list.filter((t) => t.type === "marketplace_order");
    case "featuredListing":
      return list.filter((t) => t.type === "featured_listing");
    case "eventBanner":
      return list.filter((t) => t.type === "event_banner");
    case "serviceBanner":
      return list.filter((t) => t.type === "service_banner");
    case "refunded":
      return list.filter(
        (t) =>
          t.type === "marketplace_order" &&
          String(t.status || "").toLowerCase().includes("refund")
      );
    default:
      return list;
  }
}
